import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { formatShort } from "@project/ui";
import { DashboardBackLink } from "@apps/dashboard/components/layout/DashboardBackLink";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import {
  isFcmWebConfigured,
  isWebPushRuntimeSupported,
  obtainWebFcmToken,
} from "@apps/dashboard/lib/fcm-web";
import {
  deletePushDevice,
  fetchNotificationPreferences,
  fetchPushDevices,
  homeExpenseQueryKeys,
  patchNotificationPreferences,
  patchPushDeviceEnabled,
  registerPushDevice,
} from "@apps/dashboard/lib/home-expense-api";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import type { NotificationPreferences } from "@apps/dashboard/lib/home-expense-types";
import { useCallback, useState } from "react";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

function platformName(t: (k: string) => string, platform: string): string {
  if (platform === "web") return t("dashboardUi.notificationsPage.platformWeb");
  if (platform === "ios") return t("dashboardUi.notificationsPage.platformIos");
  if (platform === "android") return t("dashboardUi.notificationsPage.platformAndroid");
  return platform;
}

export default function NotificationSettingsPage(): JSX.Element {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [pushBusy, setPushBusy] = useState(false);
  const [pushHint, setPushHint] = useState<string | null>(null);

  const invalidateNotificationQueries = useCallback(() => {
    void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.notificationsInbox(locale) });
    void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.notificationUnread(locale) });
  }, [qc, locale]);

  const prefsQuery = useQuery({
    queryKey: homeExpenseQueryKeys.notificationPreferences(locale),
    queryFn: () => fetchNotificationPreferences(locale),
  });

  const devicesQuery = useQuery({
    queryKey: homeExpenseQueryKeys.pushDevices(locale),
    queryFn: () => fetchPushDevices(locale),
  });

  const patchPrefMut = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      patchNotificationPreferences(locale, patch),
    onSuccess: (data) => {
      qc.setQueryData(homeExpenseQueryKeys.notificationPreferences(locale), data);
    },
  });

  const deleteDeviceMut = useMutation({
    mutationFn: (id: string) => deletePushDevice(locale, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.pushDevices(locale) });
    },
  });

  const patchDeviceMut = useMutation({
    mutationFn: ({ id, pushEnabled }: { id: string; pushEnabled: boolean }) =>
      patchPushDeviceEnabled(locale, id, pushEnabled),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.pushDevices(locale) });
    },
  });

  const onActivatePush = async () => {
    void trackDashboardEvent("push_activate_clicked", { platform: "web" });
    setPushHint(null);
    if (!isFcmWebConfigured()) {
      setPushHint(t("dashboardUi.notificationsPage.configMissing"));
      return;
    }
    setPushBusy(true);
    try {
      if (!(await isWebPushRuntimeSupported())) {
        setPushHint(t("dashboardUi.notificationsPage.notSupported"));
        return;
      }
      const token = await obtainWebFcmToken();
      if (!token) {
        void trackDashboardEvent("push_permission_result", {
          platform: "web",
          result:
            typeof Notification !== "undefined" && Notification.permission === "denied"
              ? "denied"
              : "dismissed_or_unavailable",
        });
        if (typeof Notification !== "undefined" && Notification.permission === "denied") {
          setPushHint(t("dashboardUi.notificationsPage.permissionDenied"));
        } else {
          setPushHint(t("dashboardUi.notificationsPage.registerError"));
        }
        return;
      }
      await registerPushDevice(locale, {
        fcmToken: token,
        platform: "web",
        appVersion: import.meta.env.VITE_APP_VERSION ?? undefined,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        locale,
        clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        pushEnabled: true,
      });
      void qc.invalidateQueries({ queryKey: homeExpenseQueryKeys.pushDevices(locale) });
      invalidateNotificationQueries();
      void trackDashboardEvent("push_permission_result", {
        platform: "web",
        result: "granted",
      });
      void trackDashboardEvent("push_device_registered", { platform: "web" });
    } catch {
      setPushHint(t("dashboardUi.notificationsPage.registerError"));
    } finally {
      setPushBusy(false);
    }
  };

  const prefToggle = (key: keyof NotificationPreferences, value: boolean) => {
    patchPrefMut.mutate({ [key]: value } as Partial<NotificationPreferences>);
  };

  const prefs = prefsQuery.data;

  return (
    <div className={DASHBOARD_PAGE_CONTAINER}>
      <DashboardBackLink to={dashboardPaths.settings}>{t("dashboardUi.settingsHub.backToAccount")}</DashboardBackLink>
      <h1 className="font-headline text-2xl font-bold tracking-tight text-primary">
        {t("dashboardUi.pages.notificationSettings")}
      </h1>
      <p className="mt-2 font-body text-body-md text-on-surface-variant">
        {t("dashboardUi.notificationSettingsPage.subtitle")}
      </p>

      <section className="mt-10 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest/40 p-5">
        <h2 className="font-headline text-sm font-bold uppercase tracking-wide text-primary">
          {t("dashboardUi.notificationsPage.pushSectionTitle")}
        </h2>
        {pushHint ? <p className="mt-3 font-body text-sm text-tertiary">{pushHint}</p> : null}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            type="button"
            variant="default"
            className="font-headline font-semibold"
            disabled={pushBusy || !isFcmWebConfigured()}
            onClick={() => void onActivatePush()}
          >
            {pushBusy ? t("common.loading") : t("dashboardUi.notificationsPage.activatePush")}
          </Button>
        </div>
        {!isFcmWebConfigured() ? (
          <p className="mt-3 font-body text-xs text-on-surface-variant">
            {t("dashboardUi.notificationsPage.configMissing")}
          </p>
        ) : (
          <p className="mt-3 font-body text-xs text-on-surface-variant">
            {t("dashboardUi.notificationsPage.pushWorking")}
          </p>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-headline text-sm font-bold uppercase tracking-wide text-primary">
          {t("dashboardUi.notificationsPage.prefsSectionTitle")}
        </h2>
        {prefsQuery.isLoading || !prefs ? (
          <p className="mt-3 font-body text-sm text-on-surface-variant">{t("common.loading")}</p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {(
              [
                ["pushMasterEnabled", "prefPushMaster"],
                ["pushHouseAndInvites", "prefHouseInvites"],
                ["pushExpenses", "prefExpenses"],
                ["pushBudget", "prefBudget"],
                ["pushReminders", "prefReminders"],
                ["pushScanAndDocs", "prefScanDocs"],
                ["pushDigests", "prefDigests"],
                ["pushUpcomingCharges", "prefUpcomingCharges"],
              ] as const
            ).map(([field, labelKey]) => (
              <li
                key={field}
                className="flex items-center justify-between gap-4 rounded-xl border border-outline-variant/15 bg-surface-container-lowest/60 px-4 py-3"
              >
                <span className="font-body text-sm text-on-surface">
                  {t(`dashboardUi.notificationsPage.${labelKey}`)}
                </span>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    className="size-4 accent-primary"
                    checked={prefs[field]}
                    disabled={patchPrefMut.isPending}
                    onChange={(e) => prefToggle(field, e.target.checked)}
                  />
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10">
        <h2 className="font-headline text-sm font-bold uppercase tracking-wide text-primary">
          {t("dashboardUi.notificationsPage.devicesSectionTitle")}
        </h2>
        {devicesQuery.isLoading ? (
          <p className="mt-3 font-body text-sm text-on-surface-variant">{t("common.loading")}</p>
        ) : !devicesQuery.data?.length ? (
          <p className="mt-3 font-body text-sm text-on-surface-variant">
            {t("dashboardUi.notificationsPage.noDevices")}
          </p>
        ) : (
          <ul className="mt-4 flex flex-col gap-3">
            {devicesQuery.data.map((d) => (
              <li
                key={d.id}
                className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest/60 px-4 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-headline text-sm font-bold text-on-surface">
                      {platformName(t, d.platform)}
                      {d.deviceModel ? ` · ${d.deviceModel}` : ""}
                    </p>
                    <p className="mt-1 font-body text-xs text-on-surface-variant">
                      {t("dashboardUi.notificationsPage.lastSeenLabel")}: {formatShort(d.lastSeenAt)}
                    </p>
                    {d.ipCity || d.ipCountryCode ? (
                      <p className="mt-0.5 font-body text-xs text-on-surface-variant/80">
                        {[d.ipCity, d.ipCountryCode].filter(Boolean).join(", ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <label className="flex items-center gap-2 font-body text-xs text-on-surface-variant">
                      <span>{t("dashboardUi.notificationsPage.devicePushLabel")}</span>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        checked={d.pushEnabled}
                        disabled={patchDeviceMut.isPending}
                        onChange={(e) =>
                          patchDeviceMut.mutate({ id: d.id, pushEnabled: e.target.checked })
                        }
                      />
                    </label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="font-headline text-xs text-tertiary"
                      disabled={deleteDeviceMut.isPending}
                      onClick={() => {
                        if (globalThis.confirm?.(t("dashboardUi.notificationsPage.removeDeviceConfirm"))) {
                          deleteDeviceMut.mutate(d.id);
                        }
                      }}
                    >
                      {t("dashboardUi.notificationsPage.removeDevice")}
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
